import React, { useContext, memo } from 'react';
import { AppContext } from 'src/components/app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ParamsAndReturnTypes } from 'src/components/translate';
import { ProductRecommendation, RecommendedProduct } from 'src/types';
import { ModelType } from './types';
import { Link } from './styles';
import { trackProductRecommendationClicked } from './tracking';
import { ProductGridCard } from 'src/components/product-card-new';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { ProductCardHorizontalScroll } from '../horizontal-scroll/variants/product-card-horizontal-scroll';

const RecommendationModel: React.FC<OwnProps> = ({
  contextId,
  link,
  recommendations,
  type,
  background,
}) => {
  const { linkTo, t } = useContext(AppContext);

  if (!recommendations?.length) {
    return null;
  }

  const handleProductClick = (product: RecommendedProduct, index: number) => {
    trackProductRecommendationClicked(contextId, product.id, type);
    trackInternalNavigationClick({
      scope: InternalNavigationScope.OTHER_VISITED_PRODUCTS_PRODUCT_CARD,
      nextPage: linkTo('product', product),
      index,
    });
  };

  const items = recommendations.map(({ product }, index) => {
    if (!product) return null;
    return (
      <ProductGridCard
        key={product.id}
        background={background}
        brand={product.brand?.name ? product.brand : undefined}
        category={product.category}
        imageSrc={product.media?.first}
        shouldLazyLoadImage
        link={linkTo('product', product)}
        name={product.name!}
        price={product.price?.regular}
        rating={product.aggregatedRating?.score || product.userReviews?.rating}
        reviewsCount={
          product.aggregatedRating?.count || product.userReviews?.count
        }
        onClick={() => handleProductClick(product, index)}
      />
    );
  });

  return (
    <Panel data-test-type="product-recommendations">
      <PanelHeader>
        <PanelTitle>
          {t(
            'product',
            `recommendations.${type as ModelType}` as keyof ParamsAndReturnTypes['product']
          )}
        </PanelTitle>
        {link && (
          <Link to={link.url} rel="nofollow" onClick={link?.onClick}>
            {link.title}
          </Link>
        )}
      </PanelHeader>

      <ProductCardHorizontalScroll negativeMargin>
        {items}
      </ProductCardHorizontalScroll>
    </Panel>
  );
};

export default memo(RecommendationModel);

type LinkType = {
  title: string;
  url: string;
  onClick?: () => void;
};

type OwnProps = {
  contextId: string;
  link?: LinkType | null;
  recommendations: ProductRecommendation[];
  type: ModelType;
  background?: boolean;
};
